import { FC, useEffect, useRef, useState } from 'react';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import { Link, useParams } from 'react-router-dom';
import { RecordValueWithSensograms, getFullRecord, updateRecordDescription, updateRecordName } from '../../services/cache/idb';
import { FlexCol, FlexRow, Paper } from '../../components/common/common';
import uPlot from 'uplot';
import { SingleSensogramFigure } from '../../components/widgets/Graph/recordSensogram';

import { Breadcrumb, Button, Col, Input, InputRef, Row, Space, Switch, Tooltip, Typography, message as antdMessage } from 'antd';

import { BoxPlotOutlined, BoxPlotTwoTone, BugOutlined, BugTwoTone, HomeOutlined, SaveOutlined, FileTextOutlined } from '@ant-design/icons';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';

import { splitTurns } from './SplitTurn';

// csv injector
import { Card } from 'antd';
import { splitFspSegments } from './splitFspSegments';
import { mergeAngleAndFsp, mergeAngleAndFspWithInterpolation } from './mergeAngleAndFsp';
import { LapSelectorPlot } from './LapSelectorPlot';
import { BoxPlotDiameter } from './BoxPlotDiameters';
import { exportDiskDiameterResultsToCsv, exportFspWithTurnToCsv } from '../../utils/helpers/csv';
import { DiskData, MergedFspSegment } from '../../types/turnFsp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface DataFrame {
  [key: string]: number[];
}

// Interfaces
interface ParsedKey {
  originalKey: string;
  prefix: string;
  letter: string;
  number: number;
  value: any;
}

interface ObjectWithBracketKeys {
  [key: string]: any;
}

export const Record: FC = () => {
  const { recordId } = useParams<{ recordId: string }>();
  const [record, setRecord] = useState<RecordValueWithSensograms | null>(null);
  const [demoRecord, setDemoRecord] = useState<RecordValueWithSensograms | null>(null);

  const targetRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const nameInputRef = useRef<InputRef>(null);
  const descriptionInputRef = useRef<InputRef>(null);

  const [showDebugInfo, setShowDebugInfo] = useState<boolean>(true);
  const [shouldAggregate, setShouldAggregate] = useState<boolean>(true);

  const [bacteria, setBacteria] = useState<string>('');
  const [antibiotics, setAntibiotics] = useState<string[]>([]);
  const [diskNames, setDiskNames] = useState<string[]>([]);

  // display demo or real record
  const [checked, setChecked] = useState<boolean>(true);

  const [mergedFspAndAngleData, setMergedFspAndAngleData] = useState<MergedFspSegment[]>([]);

  // diamter computation
  const [computedDiameters, setComputedDiameters] = useState<DiskData[]>([]);

  // helpers

  function generateArray(spotgrid: number[]): string[] {
    const letters = 'ABCDEFGHIJKLMNOP';
    const result: string[] = [];
    let index = 0;

    // Start from P (index 15) down to A (index 0)
    for (let i = letters.length - 1; i >= 0; i--) {
      for (let j = 0; j < 4; j++) {
        const label = `${letters[i]}${j}`;
        const spotValue = spotgrid[index];

        result.push(`${spotValue}[${label}]`);

        index++;
      }
    }

    return result;
  }

  function reshape(record: { sensorgramSeries: number[][] }, spotsgrid: number[]): ObjectWithBracketKeys {
    const keys = generateArray(spotsgrid);
    const reshaped: ObjectWithBracketKeys = {};

    // Initialize each key with an empty array
    for (const key of keys) {
      reshaped[key] = [];
    }

    // Loop through each timestamp
    record.sensorgramSeries.forEach((values) => {
      values.forEach((val, idx) => {
        reshaped[keys[idx]].push(val);
      });
    });

    return reshaped;
  }

  useEffect(() => {
    console.log('🎬 Component mounted, initial demoRecord:', demoRecord);
    return () => {
      console.log('🧹 Component unmounted');
    };
  }, []);

  useEffect(() => {
    getFullRecord(recordId).then((_record) => {
      setRecord(_record);
      console.log('record is', _record);
    });
  }, [recordId]);

  useEffect(() => {
    if (record === null) {
      return;
    }
    if (record !== null && record.turnTableTimestamps && record.turnTableSeries) {
      let turnSegments = splitTurns(record.turnTableTimestamps, record.turnTableSeries);
      // fill
      let spotsgrid = record.device.spotsgrid;
      if (spotsgrid.length !== 64) {
        console.warn('spotsgrid length is not 64, cannot proceed with fspSegments extraction');
        return;
      }
      let fspData: ObjectWithBracketKeys = reshape({ sensorgramSeries: record.sensogramSeries }, spotsgrid);
      let fspSegments = splitFspSegments(turnSegments, record.sensogramTimestamps, fspData);

      // Merge into angle-aligned segments
      const merged = mergeAngleAndFspWithInterpolation(turnSegments, fspSegments);
      setMergedFspAndAngleData(merged);

      if (record.description !== undefined) setDiskNames(record.description.split(','));
    }
  }, [record]);

  useEffect(() => {
    if (record === null) {
      return;
    }
    let X = record.sensogramTimestamps.map((ts) => ts / 1000);
    let Ys: number[][] = [];
    for (let i = 0; i < 64; i++) {
      let Y = [];
      for (let j = 0; j < record.sensogramSeries.length; j++) {
        Y[j] = record.sensogramSeries[j][i];
      }
      Ys[i] = Y;
    }
    let data: uPlot.AlignedData = [X, ...Ys];
    let opts: uPlot.Options = {
      width: 400,
      height: 400,
      legend: {
        show: false,
      },
      series: [
        {},
        ...Ys.map((_, i) => {
          return {
            show: true,
            stroke: `hsl(${(i * 360) / 64}, 100%, 50%)`,
            label: `S${i}`,
          };
        }),
      ],
    };

    if (targetRef.current === null) {
      return;
    }
    let uplot = new uPlot(opts, data, targetRef.current);
    uplot.setSize({
      width: targetRef.current.clientWidth,
      height: targetRef.current.clientHeight,
    });
    uplotRef.current = uplot;
    console.log('uplot', uplot);
    return () => {
      uplot.destroy();
      uplotRef.current = null;
    };
  }, [record]);

  if (!record) {
    return null;
  }

  return (
    <div tabIndex={-1}>
      <Paper>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/home">
                  <HomeOutlined />
                </Link>
              ),
            },
            {
              title: <Link to="/records">Records</Link>,
            },
            {
              title: record.name ? record.name : '—',
            },
          ]}
        />
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'end',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Tooltip overlay="Enable disk analysis">
            <Switch
              onChange={() => {
                setChecked(!checked);
              }}
              defaultChecked={true}
            />
          </Tooltip>

          <Tooltip overlay="Toggle debug info">
            <Button
              type="text"
              size="large"
              icon={showDebugInfo ? <BugTwoTone /> : <BugOutlined />}
              onClick={() => {
                setShowDebugInfo(!showDebugInfo);
              }}
            />
          </Tooltip>
          <Tooltip overlay="Toggle sensor aggregation">
            <Button
              type="text"
              size="large"
              onClick={() => {
                setShouldAggregate(!shouldAggregate);
              }}
              icon={shouldAggregate ? <BoxPlotTwoTone /> : <BoxPlotOutlined />}
            />
          </Tooltip>
          <Tooltip overlay="Export Results CSV">
            <Button
              type="text"
              size="large"
              onClick={() => {
                exportDiskDiameterResultsToCsv(computedDiameters, record, diskNames);
              }}
              icon={<FontAwesomeIcon icon={faFileExport} style={{ fontSize: '13pt' }} />}
            />
          </Tooltip>
          <Tooltip overlay="Export TimeSeries CSV">
            <Button
              type="text"
              size="large"
              onClick={() => {
                exportFspWithTurnToCsv(mergedFspAndAngleData, record);
              }}
              icon={<FontAwesomeIcon icon={faFileExport} style={{ fontSize: '13pt' }} />}
            />
          </Tooltip>
        </FlexRow>
        <FlexRow
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="p-4 space-y-4">
            {/* <Upload beforeUpload={handleFile} showUploadList={false}>
              <Button icon={<UploadOutlined />}>Inject CSV</Button>
            </Upload> */}

            <Card title="Bacteria & Antibiotics" className="shadow-md rounded-2xl">
              <div className="space-y-2">
                <Input placeholder="Bacteria" value={bacteria} onChange={(e) => setBacteria(e.target.value)} />
                {antibiotics.map((ab, idx) => (
                  <Input
                    key={idx}
                    placeholder={`Antibiotic ${idx + 1}`}
                    value={ab}
                    onChange={(e) => {
                      const newAbs = [...antibiotics];
                      newAbs[idx] = e.target.value;
                      setAntibiotics(newAbs);
                    }}
                  />
                ))}
              </div>
            </Card>
          </div>

          <FlexCol
            style={{
              width: '100%',
            }}
          >
            <b>Name:</b>
            <Space.Compact>
              <Input defaultValue={record.name} placeholder="Name" ref={nameInputRef} />
              <Button
                icon={<SaveOutlined />}
                type="primary"
                onClick={async () => {
                  if (nameInputRef.current === null || nameInputRef.current.input === null) {
                    return;
                  }
                  await updateRecordName(record.key, nameInputRef.current.input.value);
                  antdMessage.success('Name updated');
                }}
              />
            </Space.Compact>
          </FlexCol>
          <FlexCol
            style={{
              width: '100%',
            }}
          >
            <b>Description:</b>
            <Space.Compact>
              <Input defaultValue={record.description} placeholder="Description" ref={descriptionInputRef} />
              <Button
                icon={<SaveOutlined />}
                type="primary"
                onClick={async () => {
                  if (descriptionInputRef.current === null || descriptionInputRef.current.input === null) {
                    return;
                  }
                  await updateRecordDescription(record.key, descriptionInputRef.current.input.value);
                  record.description = descriptionInputRef.current.input.value;
                  setDiskNames(record.description.split(','));
                  antdMessage.success('Description updated');
                }}
              />
            </Space.Compact>
          </FlexCol>
        </FlexRow>

        {checked && <BoxPlotDiameter computedDiameters={computedDiameters} diskNames={diskNames} diskImages={record.diskImages || []} />}

        {showDebugInfo && (
          <div>
            {checked ? (
              <>
                <Row gutter={[5, 5]}>
                  <Col xs={24} lg={24}>
                    <div
                      style={{
                        width: '100%',
                      }}
                    >
                      <LapSelectorPlot mergedData={mergedFspAndAngleData} peptide={24} setComputedDiameters={setComputedDiameters} />
                    </div>
                  </Col>
                </Row>
                <Row gutter={[5, 5]}>
                  <Col xs={24} lg={24}>
                    <div
                      style={{
                        width: '100%',
                        height: 400,
                      }}
                    >
                      {demoRecord !== null && <SingleSensogramFigure record={demoRecord} shouldAggregate={shouldAggregate} />}
                    </div>
                  </Col>
                </Row>
                <FlexRow
                  style={{
                    width: '100%',
                    padding: 10,
                  }}
                >
                  <JsonView
                    data={record}
                    style={defaultStyles}
                    shouldInitiallyExpand={(level) => {
                      return level < 1;
                    }}
                  />
                </FlexRow>
              </>
            ) : (
              <Row gutter={[5, 5]}>
                <Col xs={24} lg={24}>
                  <div
                    style={{
                      width: '100%',
                      height: 400,
                    }}
                  >
                    {record !== null && <SingleSensogramFigure record={record} shouldAggregate={shouldAggregate} />}
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
};
